import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, isValid, parseISO } from 'date-fns';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  CheckCircle as DoneIcon,
  Close as CloseIcon,
  DoneAll as DoneAllIcon,
  ErrorOutline as FaultIcon,
  RadioButtonUnchecked as TodoIcon,
  CloudUpload as UploadIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { equipmentAPI } from '../../../equipment/services/equipment';
import { roomsAPI } from '../../../rooms/services/rooms';
import UploadRoomDrawingDialog from '../../../rooms/components/UploadRoomDrawingDialog';
import { FilePreviewSidebar } from '../../../files/components/FileExplorer/modules/PreviewSidebar';
import { useFilePermissions, useRoomPermissions } from '../../../auth/hooks/usePermissions';

/**
 * Equipment Inspection Step
 *
 * One card per device of the configured types, laid out like the official
 * inspection form: room and classification, model and serial number, whether
 * the room has a drawing, last/next inspection dates, and a list of checks
 * answered OK / Fejl / – with a "Justeret" note for faults.
 *
 * Options Config:
 * {
 *   "equipment_types": ["XRAY_GENERATOR_INTRAORAL", "CBCT", "PANORAMIC"],
 *   "checks": [{ "id": "arm_stable", "label": "Er røntgenarmen stabil ...?" }]
 * }
 *
 * The saved value is one record per device. Device and room details are a
 * snapshot, so a finished run still reads correctly after equipment changes.
 * [{
 *   equipment_id, name, manufacturer, model, serial_number, equipment_type,
 *   room_id, room_name, room_classification, room_drawing_file_id,
 *   last_inspection, next_inspection,
 *   checks: { [checkId]: { status: 'ok' | 'fault' | 'na', adjusted: string } }
 * }]
 */

const STATUSES = ['ok', 'fault', 'na'];

// Dates are stored as plain YYYY-MM-DD days
const toDate = (day) => (day ? parseISO(day) : null);
const toDay = (date) => format(date, 'yyyy-MM-dd');

const SNAPSHOT_FIELDS = [
  'name', 'manufacturer', 'model', 'serial_number', 'equipment_type',
  'room_id', 'room_name', 'room_classification', 'room_drawing_file_id',
];

function snapshot(equipment) {
  return {
    equipment_id: equipment.equipment_id,
    name: equipment.name,
    manufacturer: equipment.manufacturer,
    model: equipment.model,
    serial_number: equipment.serial_number,
    equipment_type: equipment.equipment_type,
    room_id: equipment.room?.room_id ?? null,
    room_name: equipment.room?.name ?? null,
    room_classification: equipment.room?.classification ?? null,
    room_drawing_file_id: equipment.room?.room_drawing_file_id ?? null,
  };
}

/**
 * What a device still needs before the inspection counts as done: every
 * check answered, a drawing of its room, and both inspection dates.
 */
export function recordMissing(record, checks) {
  const missing = [];
  const unanswered = checks.filter(c => !record.checks?.[c.id]?.status).length;
  if (unanswered) missing.push({ key: 'checks', count: unanswered });
  if (!record.room_drawing_file_id) missing.push({ key: 'drawing' });
  if (!record.last_inspection) missing.push({ key: 'last_inspection' });
  if (!record.next_inspection) missing.push({ key: 'next_inspection' });
  return missing;
}

function isRecordDone(record, checks) {
  return recordMissing(record, checks).length === 0;
}

/** How many devices are fully inspected, for the step header */
export function equipmentInspectionProgress(step, value) {
  const checks = step.options?.checks || [];
  const records = Array.isArray(value) ? value : [];
  return {
    filled: records.filter(r => isRecordDone(r, checks)).length,
    total: records.length,
  };
}

function DrawingPreviewDialog({ roomId, roomName, onClose }) {
  const { t } = useTranslation(['rooms']);
  const [file, setFile] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!roomId) return;
    let cancelled = false;
    roomsAPI.getRoomById(roomId)
      .then((res) => {
        const room = res.data?.data ?? res.data;
        if (!cancelled) setFile(room?.room_drawing ?? null);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [roomId]);

  return (
    <Dialog open={Boolean(roomId)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {t('rooms:room_drawing')} – {roomName}
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {failed && <Alert severity="error">{t('rooms:messages.no_drawing_attached')}</Alert>}
        {!failed && !file && (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        )}
        {file && <FilePreviewSidebar file={file} width="100%" sx={{ aspectRatio: '210 / 297' }} />}
      </DialogContent>
    </Dialog>
  );
}

function StatusToggle({ value, onChange, disabled }) {
  const { t } = useTranslation(['procedures']);
  const colors = { ok: 'success', fault: 'error', na: 'grey' };
  return (
    <ToggleButtonGroup
      value={value || null}
      exclusive
      size="small"
      disabled={disabled}
      onChange={(e, next) => { if (next !== null) onChange(next); }}
      sx={{ '& .MuiToggleButton-root': { px: 1.25, py: 0.25, textTransform: 'none', minWidth: 44 } }}
    >
      {STATUSES.map(status => (
        <ToggleButton
          key={status}
          value={status}
          sx={{
            '&.Mui-selected, &.Mui-selected:hover': status === 'na'
              ? { bgcolor: 'action.selected' }
              : { bgcolor: `${colors[status]}.main`, color: `${colors[status]}.contrastText` },
          }}
        >
          {t(`procedures:equipment_inspection.status.${status}`)}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

const DeviceCard = memo(function DeviceCard({ record, checks, readOnly, canUploadDrawing, onChange, onViewDrawing, onUploadDrawing }) {
  const { t } = useTranslation(['procedures', 'rooms', 'equipment']);
  const missing = recordMissing(record, checks);
  const done = missing.length === 0;
  // Only point out gaps once the device has been started on
  const touched = checks.some(c => record.checks?.[c.id]?.status) || record.last_inspection || record.next_inspection;
  const hasFault = checks.some(c => record.checks?.[c.id]?.status === 'fault');
  const emptyChecks = checks.filter(c => !record.checks?.[c.id]?.status);

  const setCheck = (checkId, patch) => onChange({
    ...record,
    checks: {
      ...record.checks,
      [checkId]: { status: '', adjusted: '', ...record.checks?.[checkId], ...patch },
    },
  });

  const setAllOk = () => onChange({
    ...record,
    checks: {
      ...record.checks,
      ...Object.fromEntries(emptyChecks.map(c => [c.id, { adjusted: '', status: 'ok' }])),
    },
  });

  const StatusIcon = hasFault ? FaultIcon : done ? DoneIcon : TodoIcon;
  const statusColor = hasFault ? 'warning.main' : done ? 'success.main' : 'text.disabled';
  // Links open in a new tab so an unsaved edit on this page isn't lost
  const linkProps = { component: RouterLink, target: '_blank', rel: 'noopener', underline: 'hover', color: 'inherit' };

  return (
    <Box
      data-equipment-card={record.room_name || record.name}
      sx={{ border: 1, borderColor: hasFault ? 'warning.main' : 'divider', borderRadius: 1, overflow: 'hidden' }}
    >
      {/* Header: where it is and what it is */}
      <Box sx={{ px: 1.5, py: 1, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
        <StatusIcon sx={{ color: statusColor, fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {record.room_id
            ? <Link {...linkProps} to={`/rooms/${record.room_id}`}>{record.room_name}</Link>
            : t('procedures:equipment_inspection.no_room')}
          {' · '}
          <Link {...linkProps} to={`/equipment/${record.equipment_id}`}>{record.name}</Link>
        </Typography>
        {record.room_classification && (
          <Chip
            size="small"
            variant="outlined"
            label={t(`rooms:classifications.${record.room_classification}`)}
          />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {record.model && `${record.model} · `}
          {t('procedures:equipment_inspection.serial', 'SN')}: {record.serial_number || '–'}
        </Typography>
      </Box>

      <Box sx={{ pt: 3, px: 1.5, pb: 1.25 }}>
        {/* Drawing and inspection dates */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 220 }}>
            <Typography variant="body2" color="text.secondary">{t('rooms:room_drawing')}:</Typography>
            {record.room_drawing_file_id ? (
              <Button size="small" startIcon={<ViewIcon />} onClick={() => onViewDrawing(record)}>
                {t('procedures:equipment_inspection.view_drawing', 'Vis')}
              </Button>
            ) : (
              <>
                <Typography variant="body2" color="error.main" sx={{ fontWeight: 500 }}>
                  {t('procedures:equipment_inspection.drawing_missing', 'Mangler')}
                </Typography>
                {canUploadDrawing && record.room_id && !readOnly && (
                  <Button size="small" startIcon={<UploadIcon />} onClick={() => onUploadDrawing(record)}>
                    {t('procedures:equipment_inspection.upload_drawing', 'Upload')}
                  </Button>
                )}
              </>
            )}
          </Box>
          <Box display="flex" gap={1.25}>
            {['last_inspection', 'next_inspection'].map(field => (
              <DatePicker
                key={field}
                label={t(`procedures:equipment_inspection.${field}`)}
                value={toDate(record[field])}
                onChange={(date) => {
                  // Half-typed dates are invalid; wait until the field holds a real day
                  if (date !== null && !isValid(date)) return;
                  onChange({ ...record, [field]: date ? toDay(date) : '' });
                }}
                disabled={readOnly}
                slotProps={{
                  textField: {
                    size: 'small',
                    // Keep the label above the field so it doesn't cover the date format
                    // (picker fields take InputLabelProps, not slotProps.inputLabel)
                    InputLabelProps: { shrink: true },
                    error: Boolean(touched && !readOnly && !record[field]),
                    sx: { width: 170 },
                  },
                }}
              />
            ))}
          </Box>
          {/* {!readOnly && emptyChecks.length > 0 && (
            <Button size="small" startIcon={<DoneAllIcon />} onClick={setAllOk} sx={{ ml: 'auto' }}>
              {t('procedures:equipment_inspection.all_ok', {
                count: emptyChecks.length,
                defaultValue: 'Resten OK ({{count}})',
              })}
            </Button>
          )} */}
        </Box>

        {/* The checks */}
        <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
          {checks.map(check => {
            const answer = record.checks?.[check.id] || {};
            return (
              <Box key={check.id} sx={{ py: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{check.label}</Typography>
                  <StatusToggle
                    value={answer.status}
                    disabled={readOnly}
                    onChange={(status) => setCheck(check.id, { status })}
                  />
                </Box>
                {(answer.status === 'fault' || answer.adjusted) && (
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    maxRows={4}
                    label={t('procedures:equipment_inspection.adjusted', 'Justeret')}
                    placeholder={t('procedures:equipment_inspection.adjusted_placeholder', 'Hvad blev rettet eller aftalt?')}
                    value={answer.adjusted || ''}
                    onChange={(e) => setCheck(check.id, { adjusted: e.target.value })}
                    disabled={readOnly}
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            );
          })}
        </Stack>

        {touched && !done && !readOnly && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
            {t('procedures:equipment_inspection.missing.label', 'Mangler')}:{' '}
            {missing.map(m => t(`procedures:equipment_inspection.missing.${m.key}`, { count: m.count })).join(' · ')}
          </Typography>
        )}

      </Box>
    </Box>
  );
});

export default function EquipmentInspectionStep({ step, value, onChange, disabled, preview }) {
  const { t } = useTranslation(['procedures', 'equipment']);
  const options = step.options || {};
  const checks = useMemo(() => options.checks || [], [options.checks]);
  const types = useMemo(() => options.equipment_types || [], [options.equipment_types]);
  const readOnly = disabled || preview;
  const saved = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const { canUpdate: canUpdateRoom } = useRoomPermissions();
  const { canUpload: canUploadFiles } = useFilePermissions();

  const [live, setLive] = useState(null);
  const [error, setError] = useState(false);
  const [previewRecord, setPreviewRecord] = useState(null);
  const [uploadRecord, setUploadRecord] = useState(null);

  // A finished run shows what was recorded; only an open one follows Udstyr
  const followLive = !disabled || saved.length === 0;

  const loadEquipment = useCallback(async () => {
    try {
      const res = await equipmentAPI.filterEquipment({ list_type: 'inventory' });
      const all = res.data?.equipment ?? res.data?.data?.equipment ?? [];
      setLive(all.filter(e => types.includes(e.equipment_type)));
      setError(false);
    } catch {
      setError(true);
    }
  }, [types]);

  useEffect(() => {
    if (followLive) loadEquipment();
  }, [followLive, loadEquipment]);

  const records = useMemo(() => {
    if (!followLive) return saved;
    if (!live) return [];
    const savedById = Object.fromEntries(saved.map(r => [r.equipment_id, r]));
    const current = live
      .slice()
      .sort((a, b) => `${a.room?.name ?? ''}${a.name}`.localeCompare(`${b.room?.name ?? ''}${b.name}`, 'da', { numeric: true }))
      .map((e) => {
        const prev = savedById[e.equipment_id];
        const fresh = snapshot(e);
        // Keep the saved object when nothing about the device changed, so
        // untouched cards skip re-rendering
        if (prev && SNAPSHOT_FIELDS.every(f => (prev[f] ?? null) === (fresh[f] ?? null))) return prev;
        return {
          last_inspection: '',
          next_inspection: '',
          checks: {},
          ...prev,
          ...fresh,
        };
      });
    // Devices removed from Udstyr after they were inspected stay in the record
    const liveIds = new Set(live.map(e => e.equipment_id));
    return [...current, ...saved.filter(r => !liveIds.has(r.equipment_id))];
  }, [followLive, live, saved]);

  const recordsRef = useRef(records);
  recordsRef.current = records;
  const handleRecordChange = useCallback((next) => {
    onChange(recordsRef.current.map(r => (r.equipment_id === next.equipment_id ? next : r)));
  }, [onChange]);

  const emptyTotal = records.reduce((sum, r) => sum + checks.filter(c => !r.checks?.[c.id]?.status).length, 0);
  const handleAllOk = () => {
    onChange(records.map(r => ({
      ...r,
      checks: {
        ...r.checks,
        ...Object.fromEntries(
          checks.filter(c => !r.checks?.[c.id]?.status).map(c => [c.id, { adjusted: '', status: 'ok' }]),
        ),
      },
    })));
  };

  const handleDrawingUploaded = (uploadResult) => {
    const fileId = uploadResult?.data?.data?.file_id;
    const roomId = uploadRecord?.room_id;
    setUploadRecord(null);
    if (!fileId || !roomId) return;
    setLive(prev => prev?.map(e => (
      e.room?.room_id === roomId ? { ...e, room: { ...e.room, room_drawing_file_id: fileId } } : e
    )));
    // Record the drawing in the saved answer too, once there is one
    if (saved.length > 0) {
      onChange(records.map(r => (r.room_id === roomId ? { ...r, room_drawing_file_id: fileId } : r)));
    }
  };

  if (preview) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('procedures:equipment_inspection.preview_types', 'Udstyr')}: {types.map(type => t(`equipment:equipment_types.${type}`)).join(', ')}
        </Typography>
        <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2.5 }}>
          {checks.map(c => <Typography component="li" variant="body2" key={c.id}>{c.label}</Typography>)}
        </Stack>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{t('procedures:equipment_inspection.load_failed', 'Udstyret kunne ikke hentes')}</Alert>;
  }

  if (followLive && !live) {
    return <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>;
  }

  if (records.length === 0) {
    return (
      <Alert severity="info">
        {t('procedures:equipment_inspection.no_equipment', {
          types: types.map(type => t(`equipment:equipment_types.${type}`)).join(', '),
          defaultValue: 'Der er intet udstyr af typen {{types}}. Tilføj det under Udstyr.',
        })}
      </Alert>
    );
  }

  return (
    <Box>
      {/* {!readOnly && emptyTotal > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button size="small" startIcon={<DoneAllIcon />} onClick={handleAllOk}>
            {t('procedures:equipment_inspection.fill_remaining_ok', {
              count: emptyTotal,
              defaultValue: 'Sæt resterende til OK ({{count}})',
            })}
          </Button>
        </Box>
      )} */}
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' } }}>
        {records.map(record => (
          <DeviceCard
            key={record.equipment_id}
            record={record}
            checks={checks}
            readOnly={readOnly}
            canUploadDrawing={canUpdateRoom && canUploadFiles}
            onChange={handleRecordChange}
            onViewDrawing={setPreviewRecord}
            onUploadDrawing={setUploadRecord}
          />
        ))}
      </Box>

      {previewRecord && (
        <DrawingPreviewDialog
          roomId={previewRecord.room_id}
          roomName={previewRecord.room_name}
          onClose={() => setPreviewRecord(null)}
        />
      )}
      <UploadRoomDrawingDialog
        open={Boolean(uploadRecord)}
        onClose={() => setUploadRecord(null)}
        roomId={uploadRecord?.room_id}
        roomName={uploadRecord?.room_name}
        onSuccess={handleDrawingUploaded}
      />
    </Box>
  );
}
